/**
 * useCritiqueModeHandlers - Event handlers for critique mode
 * Feature: Refactoring CollectionCritiqueModePage
 *
 * Consolidates all event handlers for:
 * - Session editing (title, description, deletion, resuming)
 * - Navigation (critique clicks, comment clicks, photo selection)
 * - View modes (dimmed lights, fullscreen)
 */

import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import logger from '@/utils/logger';
import { critiqueSessionUrl } from '@/utils/urls';
import type { ViewMode } from '@/contexts/ViewModeContext';
import type { PhotoItem2ViewModel } from '@/components/PhotoItem2/PhotoItem2.types';
import type { Collection } from '@/graphql/types';
import type { User } from '@/types';
import type { CritiqueUrlState } from '@/types/critiqueUrlState';

// Flexible session type matching actual query response fields
export type CritiqueSessionData = {
  id: string;
  globalId: string;
  title: string;
  slug: string;
  description?: string | null;
  collectionId: string;
  isAiSession?: boolean;
  isPaused?: boolean;
  aiFeedbackStatus?: {
    pending: number;
    completed: number;
    failed: number;
    total: number;
    isComplete: boolean;
  } | null;
  // Feature 649: Feedback options
  feedbackOptions?: {
    enabled: string[];
    hasGeneral: boolean;
    hasRating: boolean;
  } | null;
  createdAt?: string;
  updatedAt?: string;
  intent?: 'open' | 'ai_only';
};

export interface CritiqueModeHandlersParams {
  // Session data
  session: CritiqueSessionData | null | undefined;
  collection: Collection | undefined;
  photos: PhotoItem2ViewModel[];
  user: User | null;

  // URL state
  urlState: CritiqueUrlState;

  // Navigation functions
  selectPhoto: (photoId: string) => void;
  selectComment: (photoId: string, commentId: string, shouldScroll: boolean) => void;
  navigate: NavigateFunction;

  // View mode
  setViewMode: (mode: ViewMode | ((prev: ViewMode) => ViewMode)) => void;

  // GraphQL mutations (accept actual mutation signatures)
  updateCritiqueSession: (input: any) => Promise<any>;
  deleteCritiqueSession: (id: string) => Promise<any>;
  resumeAICritiqueSession: (input: any) => Promise<any>;
}

export function useCritiqueModeHandlers(params: CritiqueModeHandlersParams) {
  const {
    session,
    collection,
    photos,
    user,
    urlState,
    selectPhoto,
    selectComment,
    navigate,
    setViewMode,
    updateCritiqueSession,
    deleteCritiqueSession,
    resumeAICritiqueSession,
  } = params;

  // Session title editing (Feature 056, Feature 065: GraphQL)
  const handleTitleChange = useCallback(async (newTitle: string) => {
    if (!session) {
      logger.error('CritiqueSession', 'Cannot update title: session not loaded');
      return;
    }

    try {
      const updated = await updateCritiqueSession({
        id: session.globalId,
        title: newTitle,
      });

      if (updated) {
        logger.info('CritiqueSession', 'Title updated, navigating to new URL');
        // Update URL with new slug
        navigate(critiqueSessionUrl(updated.globalId, updated.slug), { replace: true });
      }
    } catch (error) {
      logger.error('CritiqueSession', 'Failed to update title:', error);
      throw error; // Re-throw to trigger EditableTitle rollback
    }
  }, [session, updateCritiqueSession, navigate]);

  // Session description editing (Feature 070: GraphQL with description support)
  const handleDescriptionChange = useCallback(async (newDescription: string | null) => {
    if (!session) {
      logger.error('CritiqueSession', 'Cannot update description: session not loaded');
      return;
    }

    try {
      logger.info('CritiqueSession', 'Updating description via GraphQL:', newDescription);
      await updateCritiqueSession({
        id: session.globalId,
        description: newDescription,
      });
      logger.info('CritiqueSession', 'Description updated successfully');
    } catch (error) {
      logger.error('CritiqueSession', 'Failed to update description:', error);
      throw error; // Re-throw to trigger EditableDescription rollback
    }
  }, [session, updateCritiqueSession]);

  // Session deletion (Feature 056, Feature 065: GraphQL)
  const handleDeleteSession = useCallback(async () => {
    if (!session) {
      logger.error('CritiqueSession', 'Cannot delete: session not loaded');
      return;
    }

    // Count comments for confirmation message
    const commentCount = photos.reduce((sum: number, photo: PhotoItem2ViewModel) => sum + (photo.critiques?.length || 0), 0);

    const confirmMessage = commentCount > 0
      ? `Delete "${session.title}" and all ${commentCount} comment${commentCount === 1 ? '' : 's'}? This cannot be undone.`
      : `Delete "${session.title}"? This cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteCritiqueSession(session.globalId);
      logger.info('CritiqueSession', 'Session deleted, redirecting to My Critiques');
      navigate('/crit'); // Redirect to My Critiques page
    } catch (error) {
      logger.error('CritiqueSession', 'Failed to delete session:', error);
      alert('Failed to delete session. Please try again.');
    }
  }, [session, photos, deleteCritiqueSession, navigate]);

  // Resume paused AI critique session (Feature 071 Phase 10)
  const handleResumeSession = useCallback(async () => {
    if (!session) {
      logger.error('CritiqueSession', 'Cannot resume: session not loaded');
      return;
    }

    try {
      logger.info('CritiqueSession', 'Resuming AI critique session:', session.globalId);
      await resumeAICritiqueSession({
        sessionId: session.globalId,
      });
      logger.info('CritiqueSession', 'AI critique session resumed, feedback generation started');
      // The polling will automatically pick up the new status
    } catch (error) {
      logger.error('CritiqueSession', 'Failed to resume AI critique session:', error);
      alert('Failed to start AI feedback generation. Please try again.');
    }
  }, [session, resumeAICritiqueSession]);

  // Handle critique card clicks to navigate to photo and comment (040-critique-url-state)
  const handleCritiqueClick = useCallback((photoId: string, commentId: string) => {
    selectComment(photoId, commentId, true); // shouldScroll=true for smooth navigation
  }, [selectComment]);

  // Handle comment clicks in the panel to update URL (040-critique-url-state)
  const handleCommentClick = useCallback((commentId: string) => {
    if (urlState.photoId) {
      // Toggle comment selection - deselect if clicking same comment
      if (urlState.commentId === commentId) {
        selectPhoto(urlState.photoId); // Remove comment param by selecting photo only
      } else {
        selectComment(urlState.photoId, commentId, false); // shouldScroll=false since already visible
      }
    }
  }, [urlState.photoId, urlState.commentId, selectPhoto, selectComment]);

  // Handle comment submitted - navigate to the new comment
  const handleCommentSubmitted = useCallback((photoId: string, commentId: string) => {
    selectComment(photoId, commentId, true); // shouldScroll=true to scroll to new comment
  }, [selectComment]);

  // View mode handlers (Feature 057-lightbox-fullscreen-oneup)
  // Dimmed lights uses debounce to prevent flicker from rapid key presses
  const toggleDimmedLights = useDebouncedCallback(() => {
    setViewMode((prev) => (prev === 'dimmedLights' ? 'normal' : 'dimmedLights'));
  }, 150); // 150ms debounce per spec

  // Fullscreen toggle handler
  // ViewModeProvider handles Fullscreen API calls automatically
  // No debounce - allows instant response for quick toggle changes
  const toggleFullscreen = useCallback(() => {
    setViewMode((prev) => (prev === 'fullscreen' ? 'normal' : 'fullscreen'));
  }, [setViewMode]);

  const exitViewMode = useCallback(() => {
    setViewMode('normal');
  }, [setViewMode]);

  // Check if current user is the session owner
  const isSessionOwner = user && collection && user.id === collection.userId;
  // today these are the same (isSessionOwner and isCollectionOwner), but in the future they may diverge
  const isCollectionOwner = user && collection && user.id === collection.userId;

  return {
    handleTitleChange,
    handleDescriptionChange,
    handleDeleteSession,
    handleResumeSession,
    handleCritiqueClick,
    handleCommentClick,
    handleCommentSubmitted,
    toggleDimmedLights,
    toggleFullscreen,
    exitViewMode,
    isSessionOwner,
    isCollectionOwner,
  };
}
